import { Dispatch } from "react";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { size } from "lodash-es";
import { CancelToken } from "axios";

import { IStoreState } from "App/types";
import { BandService } from "Bands/services";
import { IBand, BAND_ACTIONS, BandSettingsDataKeys, IEditItem } from "Bands/types";
import { logout } from "Auth/state";
import { API_ENDPOINTS } from "shared/constants";
import { IDataKeys } from "shared/types";
import { getBandSettingActionFromKey } from "Bands/utils";

export const dispatchGetAdminBands = (
  dispatch: Dispatch<any>,
  page?: number,
  sortBy?: string,
  sortDirection?: string
) => {
  dispatch(
    getAdminBands({
      page: page || 1,
      paginationKey: BAND_ACTIONS.getAdminBands,
      sortBy,
      sortDirection,
    })
  );
};

export const getAdminBands = createAsyncThunk(
  BAND_ACTIONS.getAdminBands,
  async (
    {
      page,
      sortBy = "title",
      sortDirection = "asc",
    }: {
      page: number;
      paginationKey: string;
      sortBy?: string;
      sortDirection?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const data = await BandService.getBands({
        page,
        itemsPerPage: 50,
        [`order[${sortBy}]`]: sortDirection,
      });

      // keep the key even if response is empty
      return {
        [IDataKeys.adminBands]: data.band,
        meta: data.meta,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const dispatchGetUserBands = (dispatch: Dispatch<any>, page?: number) => {
  dispatch(
    getUserBands({
      page: page || 1,
      paginationKey: BAND_ACTIONS.getUserBands,
    })
  );
};

export const getUserBands = createAsyncThunk(
  BAND_ACTIONS.getUserBands,
  async (
    { page }: { page: number; paginationKey: string },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const user = (getState() as IStoreState).auth.currentUser?.id;

      // nest try catch so user does not logout when getBands call returns 401
      try {
        if (user) {
          const data = await BandService.getBands({
            user,
            page,
            "order[title]": "asc",
            itemsPerPage: 100,
          });

          // keep the key even if response is empty
          return {
            [IDataKeys.userBands]: data.band,
            meta: data.meta,
            pagination: data.pagination,
          };
        }
      } catch (error) {
        return rejectWithValue(error);
      }
    } catch (error) {
      // if user token is not valid
      // user is probably deleted on another device
      if ((error as any).status === 401) {
        dispatch(logout());
      }

      return rejectWithValue(error);
    }
  }
);

export const dispatchGetBandAction = (
  dispatch: Dispatch<any>,
  bandId: string,
  cancelToken?: CancelToken
) => {
  const action = API_ENDPOINTS.BAND(bandId);
  return dispatch(getBand({ bandId, action, cancelToken }));
};

export const getBand = createAsyncThunk(
  BAND_ACTIONS.getBand,
  async (
    { bandId, cancelToken }: { bandId: string; action: string; cancelToken?: CancelToken },
    { rejectWithValue }
  ) => {
    try {
      const band = await BandService.getBand(bandId, cancelToken);

      return band;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createBand = createAsyncThunk(
  BAND_ACTIONS.createBand,
  async (band: Partial<IBand>, { rejectWithValue }) => {
    try {
      const data = await BandService.createBand(band);
      const id = Object.values(data?.band)?.[0]?.attributes?.id;
      return { id };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editBand = createAsyncThunk(
  BAND_ACTIONS.editBand,
  async (
    { bandId, bandData }: { bandId: string; bandData: Partial<IBand> },
    { rejectWithValue }
  ) => {
    try {
      return await BandService.editBand(bandId, bandData);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteBand = createAsyncThunk(
  BAND_ACTIONS.deleteBand,
  async (bandId: string, { rejectWithValue }) => {
    try {
      await BandService.deleteBand(bandId);
      return { path: API_ENDPOINTS.BAND(bandId), dataKey: IDataKeys.band };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const isOnlyBandUser = createAsyncThunk(
  BAND_ACTIONS.isOnlyBandUser,
  async ({ userId, bandId }: { userId: string; bandId?: string }, { rejectWithValue }) => {
    try {
      const response = await BandService.getBands({
        user: userId,
      });

      if (size(response.band) === 0) {
        return;
      }

      const includesBand = bandId
        ? Object.keys(response.band).includes(API_ENDPOINTS.BAND(bandId))
        : false;

      // if specific band is specified
      if (includesBand) {
        return rejectWithValue("User is only band user for a band");
      }

      // if no band is specified, check for any bands
      if (size(response.band) !== 0) {
        return rejectWithValue("User is only band user for a band");
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const editBandSettingsActionCreator = async ({
  dispatch,
  ...props
}: {
  oldItems: IEditItem[];
  newItems: IEditItem[];
  dataKey: BandSettingsDataKeys;
  bandId: string;
  dispatch: Dispatch<any>;
}) => {
  const action = getBandSettingActionFromKey(props.dataKey);

  const asyncThunk = createAsyncThunk(
    action,
    async (
      props: {
        oldItems: IEditItem[];
        newItems: IEditItem[];
        dataKey: BandSettingsDataKeys;
        bandId: string;
      },
      { rejectWithValue }
    ) => {
      try {
        const data = await BandService.editBandSettings(props);
        return data;
      } catch (error) {
        return rejectWithValue(error);
      }
    }
  );

  return dispatch(asyncThunk(props));
};
